import type { CSSProperties } from "react";
import type { CtaAlignment } from "@/utils/ctaAlignment";

export type BlogLineTextVariant = "label" | "heading" | "custom";
export type BlogLineTextAlign = CtaAlignment;
export type BlogLineTextMarginRem = 0 | 1 | 2;
export type BlogLineTextColor = "black" | "blue";

const DEFAULT_FONT_SIZE_REM: Record<BlogLineTextVariant, number> = {
  label: 0.75,
  heading: 1.875,
  custom: 1,
};

const VARIANT_CLASS: Record<BlogLineTextVariant, string> = {
  label: "uppercase tracking-[0.12em] font-medium",
  heading: "font-bold",
  custom: "font-normal",
};

const COLOR_CLASS: Record<BlogLineTextColor, string> = {
  black: "text-foreground",
  blue: "text-gail-primary",
};

const ALIGN_CLASS: Record<BlogLineTextAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function normalizeBlogLineTextAlign(
  align: unknown,
): BlogLineTextAlign {
  if (align === "center" || align === "right") return align;
  return "left";
}

export function getDefaultBlogLineTextFontSizeRem(
  variant: BlogLineTextVariant,
): number {
  return DEFAULT_FONT_SIZE_REM[variant];
}

export function resolveBlogLineTextFontSizeRem(
  variant: BlogLineTextVariant,
  fontSizeRem: number,
): number {
  if (fontSizeRem > 0) return fontSizeRem;
  return DEFAULT_FONT_SIZE_REM[variant];
}

export function resolveBlogLineTextMaxWidthRem(maxWidthRem: number): number | undefined {
  if (!(maxWidthRem > 0)) return undefined;
  return maxWidthRem;
}

export function getBlogLineTextClassName(
  variant: BlogLineTextVariant,
  color: BlogLineTextColor = "black",
  italic = false,
  align: BlogLineTextAlign = "left",
): string {
  return [
    "not-prose m-0 font-monument leading-tight",
    VARIANT_CLASS[variant],
    COLOR_CLASS[color],
    ALIGN_CLASS[align],
    italic ? "italic" : null,
  ]
    .filter(Boolean)
    .join(" ");
}

export function getBlogLineTextStyle(
  fontSizeRem: number,
  marginRem: BlogLineTextMarginRem,
  maxWidthRem = 0,
): CSSProperties {
  const resolvedMaxWidthRem = resolveBlogLineTextMaxWidthRem(maxWidthRem);

  return {
    fontSize: `${fontSizeRem}rem`,
    margin: marginRem === 0 ? 0 : `${marginRem}rem`,
    ...(resolvedMaxWidthRem !== undefined
      ? { maxWidth: `${resolvedMaxWidthRem}rem` }
      : {}),
  };
}

export function resolveBlogLineTextPresentation(
  variant: BlogLineTextVariant,
  fontSizeRem: number,
  marginRem: BlogLineTextMarginRem = 0,
  color: BlogLineTextColor = "black",
  italic = false,
  maxWidthRem = 0,
  align: BlogLineTextAlign = "left",
): { className: string; style: CSSProperties } {
  const resolvedSize = resolveBlogLineTextFontSizeRem(variant, fontSizeRem);
  return {
    className: getBlogLineTextClassName(variant, color, italic, align),
    style: getBlogLineTextStyle(resolvedSize, marginRem, maxWidthRem),
  };
}
